import React, { useEffect, useRef, useState } from "react";
import FieldTableRow from "./FieldTableRow";
import _ from "underscore";
import API from "../lib/TimeEditAPI";
import Lang from "../lib/Language";
import { useAppContext } from "../contexts/AppContext";

const SCROLL_HEIGHT_MOD = 78;

type TSelectionListFieldEditorProps = {
    fields: any[];
    fluffy: any;
    height: number;
    disableClear: boolean;
    clearFieldValues: React.MouseEventHandler;
};

const SelectionListFieldEditor = (props: TSelectionListFieldEditorProps) => {
    const appContext = useAppContext();
    const [state, setState] = useState<{ fields: any[]; fieldDefs: any[] }>({
        fields: [],
        fieldDefs: [],
    });

    const loadFields = () => {
        const fieldIds = props.fields.map((fld) => fld.id);
        API.getFields(fieldIds, (fields) => {
            API.getFieldDefs(fieldIds, undefined, (defs) => {
                setState({ fieldDefs: defs, fields });
            });
        });
    };

    // componentDidMount plus a componentDidUpdate guarded on a deep compare of
    // `fields`. The parent rebuilds that array every render (.map over fieldItems),
    // so a dependency array would refire these two API calls continuously.
    const prevFields = useRef<any[] | null>(null);
    useEffect(() => {
        if (prevFields.current === null || !_.isEqual(prevFields.current, props.fields)) {
            prevFields.current = props.fields;
            loadFields();
        }
    });

    const updateField = (field, updateFn) => {
        let fieldItem = _.find(props.fields, (item) => item.id === field.id);
        const newValues = [].concat(fieldItem.values || []);
        updateFn(newValues);
        fieldItem = _.extend({}, fieldItem, { values: newValues });
        appContext.update(props.fluffy, props.fluffy.setFields([fieldItem]));
    };

    const addField = (field) => {
        updateField(field, (values) => {
            if (values.length === 0) {
                // From the user"s perspective, zero values are displayed as one empty value. Thus we need to go from 0 -> 2.
                values.push("");
            }
            values.push("");
        });
    };

    const removeField = (field, index) => {
        updateField(field, (values) => values.splice(index, 1));
    };

    const setFieldValue = (field, event, newValue, valueIndex) => {
        updateField(field, (values) => {
            // eslint-disable-next-line no-param-reassign
            values[valueIndex] = newValue;
        });
    };

    const getFieldValues = (fieldId) => {
        const field = _.find(props.fields, (fld) => fld.id === fieldId);
        if (!field) {
            return [];
        }
        return field.values;
    };

    const fields: React.ReactNode[] = state.fields
        .map((field, index) => {
            const definition = _.find(state.fieldDefs, (def) => def.id === field.id);
            if (!definition) {
                return null;
            }
            if (!definition.editable) {
                return null;
            }
            const values = getFieldValues(field.id);
            if (values.length === 0) {
                return null;
            }
            const valuedField = _.extend({}, field, {
                values,
            });
            const indeterminate = false;
            return (
                <FieldTableRow
                    key={field.id}
                    isFirstField={index === 0}
                    editable={false}
                    isCompact={true}
                    field={valuedField}
                    definition={definition}
                    indeterminate={indeterminate}
                    addField={() => addField(field)}
                    removeField={(index2) => removeField(field, index2)}
                    setFieldValue={(event, newValue, valueIndex) =>
                        setFieldValue(field, event, newValue, valueIndex)
                    }
                />
            );
        })
        .filter((fld) => fld !== null);
    if (fields.length === 0) {
        fields.push(
            <tbody key={"emptyState"}>
                <tr key={"emptyState"}>
                    <td style={{ width: "100%", position: "relative", padding: "10px" }}>
                        {Lang.get("nc_field_list_empty")}
                    </td>
                </tr>
            </tbody>
        );
    }
    return (
        <div className={"selectFieldBox"} id="selectionListFields">
            <div className={"listTopLine"}>
                <h1 style={{ flexGrow: "1" }}>{Lang.get("cal_reservation_list_column_field")}</h1>
                <button
                    className={"deprecated editGroupButton save"}
                    title={Lang.get("cal_res_below_clear")}
                    onClick={props.clearFieldValues}
                    disabled={props.disableClear || props.fluffy.allFieldsEmpty()}
                >
                    {Lang.get("nc_dynamic_object_list_clear_search_settings")}
                </button>
            </div>
            <div
                style={{
                    overflowY: "scroll",
                    height: `${props.height - SCROLL_HEIGHT_MOD}px`,
                }}
            >
                <table>{fields}</table>
            </div>
        </div>
    );
};

export default SelectionListFieldEditor;
